import type { DrawerConfig, LayoutMachineContext, LayoutMachineEvents } from 'BomdisoftComponents/layout/types';

export const INITIAL_LAYOUT_STATE: LayoutMachineContext = {
  drawers: {},
  navPosition: 'bottom',
  sidebarOpen: false,
};

const clampWidth = (drawer: DrawerConfig, width: number) => Math.min(drawer.maxWidth, Math.max(drawer.minWidth, width));

const withDrawer = (state: LayoutMachineContext, id: string, next: (drawer: DrawerConfig) => DrawerConfig): LayoutMachineContext => {
  const drawer = state.drawers[id];
  if (!drawer) return state;
  return { ...state, drawers: { ...state.drawers, [id]: next(drawer) } };
};

/**
 * The layout state, as a plain reducer.
 *
 * This was an xstate machine, but it had no states, no guards, no actors and no
 * transitions — only a context and a list of events that assign to it, which is
 * a reducer wearing a costume. It cost every app that renders NavigationLayout
 * ~136KB of xstate in its initial bundle to do what fifty lines do here.
 */
export const layoutReducer = (state: LayoutMachineContext, event: LayoutMachineEvents): LayoutMachineContext => {
  switch (event.type) {
    case 'CLOSE_DRAWER':
      return withDrawer(state, event.id, drawer => ({ ...drawer, open: false }));
    case 'OPEN_DRAWER':
      return withDrawer(state, event.id, drawer => ({ ...drawer, open: true }));
    // Registration resets `open`, matching the machine it replaces: a drawer
    // whose config changes is treated as a fresh drawer.
    case 'REGISTER_DRAWER':
      return { ...state, drawers: { ...state.drawers, [event.id]: { ...event.config, open: false } } };
    case 'RESIZE_DRAWER':
      return withDrawer(state, event.id, drawer => ({ ...drawer, width: clampWidth(drawer, event.width) }));
    case 'SET_SIDEBAR':
      return { ...state, sidebarOpen: event.open };
    case 'TOGGLE_DRAWER':
      return withDrawer(state, event.id, drawer => ({ ...drawer, open: !drawer.open }));
    case 'TOGGLE_SIDEBAR':
      return { ...state, sidebarOpen: !state.sidebarOpen };
    case 'UNREGISTER_DRAWER': {
      if (!state.drawers[event.id]) return state;
      const { [event.id]: _removed, ...rest } = state.drawers;
      return { ...state, drawers: rest };
    }
    default:
      return state;
  }
};
